import { Injectable } from '@angular/core';
import { get } from 'lodash';
import { MenuItem } from 'primeng/api';

import { PANELMENUTYPES } from './panel-menu.const';

import { MeetingItem } from '~/repositories/meeting/store/meeting.models';

@Injectable({
  providedIn: 'root'
})
export class PanelMenuService {
  public toggleItem(item: MenuItem, expanded: boolean) {
    item.expanded = expanded;
  }

  public toggleSummary(item: MenuItem, showSummary: boolean) {
    item['showSummary'] = showSummary;
  }

  public toggleAll(items: MenuItem[], expanded: boolean): void {
    items.forEach((item) => {
      if (item.items) {
        this.toggleAll(item.items, expanded);
      }

      item.expanded = expanded;
    });
  }

  public toggleAllSummary(items: MeetingItem[], expanded: boolean, prevItems?: MeetingItem[]): MeetingItem[] {
    items.forEach((item, index) => {
      const prevItemState = get(prevItems, index, null);

      if (item.items) {
        this.toggleAllSummary(item.items, expanded, prevItemState?.items);
      }

      if ((prevItemState?.id, prevItemState?.expanded)) {
        item.expanded = prevItemState?.expanded;
      }

      if (item.type !== PANELMENUTYPES.FOLDER && !item.noSummaryAccess) {
        item.showSummary = expanded;
      }
    });

    return items;
  }

  public selectItem(item: MenuItem, menuItems: MenuItem[], checked: boolean) {
    this.updateItemCheckedState(menuItems, item['key'], checked);
    this.updateAllParentStates(menuItems);
  }

  private updateItemCheckedState(items: MenuItem[], targetKey: string, checked: boolean): boolean {
    for (let i = 0; i < items.length; i++) {
      if (items[i]['key'] === targetKey) {
        items[i] = { ...items[i], checked };
        if (items[i].items) {
          items[i].items = this.setAllChildrenChecked(items[i].items, checked);
        }
        return true;
      }
      if (items[i].items) {
        const found = this.updateItemCheckedState(items[i].items, targetKey, checked);
        if (found) {
          items[i] = { ...items[i] };
        }
      }
    }
    return false;
  }

  private setAllChildrenChecked(items: MenuItem[], checked: boolean): MenuItem[] {
    return items.map((item) => ({
      ...item,
      checked,
      items: item.items ? this.setAllChildrenChecked(item.items, checked) : item.items
    }));
  }

  public selectAllItems(menuItems: MenuItem[], checked: boolean): MenuItem[] {
    return menuItems.map((item) => ({
      ...item,
      checked,
      items: item.items ? this.selectAllItems(item.items, checked) : item.items
    }));
  }

  public getCheckedItems(menuItems: MenuItem[]): MenuItem[] {
    if (!menuItems) return [];
    const checkedItems: MenuItem[] = [];
    menuItems.forEach((item) => {
      if (item.items) {
        checkedItems.push(...this.getCheckedItems(item.items));
      }
      if (item['checked']) {
        checkedItems.push(item);
      }
    });
    return checkedItems;
  }

  public getExpandedItems(menuItems: MenuItem[]): MenuItem[] {
    if (!menuItems) return [];
    const expandedItems: MenuItem[] = [];
    menuItems.forEach((item) => {
      if (item.items) {
        expandedItems.push(...this.getExpandedItems(item.items));
      }
      if (item['expanded']) {
        expandedItems.push(item);
      }
    });
    return expandedItems;
  }

  public updateSingleItem(item: MenuItem, items: MenuItem[]): MenuItem[] {
    return items.map((i) => {
      if (i['key'] === item['key']) {
        return {
          ...i,
          ...item
        };
      } else if (i.items?.length) {
        return {
          ...i,
          items: this.updateSingleItem(item, i.items)
        };
      } else {
        return i;
      }
    });
  }

  public areAllItemsChecked(menuItems: MenuItem[]): boolean {
    if (!menuItems?.length) return false;
    return menuItems.every((item) =>
      item.items?.length ? item['checked'] && this.areAllItemsChecked(item.items) : item['checked']
    );
  }

  private updateAllParentStates(items: MenuItem[]) {
    items.forEach((item) => {
      if (item.items?.length) {
        this.updateAllParentStates(item.items);
        const allChildrenChecked = item.items.every((child) => child['checked']);
        if (item['checked'] !== allChildrenChecked) {
          Object.assign(item, { ...item, checked: allChildrenChecked });
        }
      }
    });
  }
}
